/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useTheme } from 'styled-components'
import { ThreeDot } from 'react-loading-indicators'
import { FaSearch, FaCheck, FaChevronDown, FaDesktop, FaFileArchive, FaCalendarAlt, FaChartLine, FaBolt, FaLayerGroup, FaThList } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { carregarDistribuidores } from '../../services/dists'
import { buscarCidadesPorDistribuidores } from './service'
import { processarRevisaoEmGrupo } from './exportRevisaoGrupo'
import { ValidacaoSelloutParams } from './types'
import {
  FilterCard,
  FilterTitle,
  FilterSubtitle,
  FilterSection,
  FilterLabel,
  FilterDivider,
  DistList,
  DistItem,
  CheckboxBox,
  SemanasGrid,
  SemanaCheckbox,
  ChipsRow,
  Chip,
  SelectRow,
  StyledSelect,
  SearchInput,
  GenerateButton,
  ButtonSpinner,
  SelectAllBtn,
  PageWrapper,
  SplitButtonWrapper,
  SplitMainButton,
  SplitDropdownToggle,
  SplitDropdownMenu,
  SplitDropdownItem,
  GroupProgressModalOverlay,
  GroupProgressModalCard,
  ProgressBarTrack,
  ProgressBarFill,
  PeriodTypeContainer,
  PeriodTypeButton,
  YtdPresetsRow,
  YtdPresetButton,
  MonthsGrid,
  MonthChip,
} from './styles'

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril',
  'Maio', 'Junho', 'Julho', 'Agosto',
  'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

interface Props {
  onGenerate: (params: ValidacaoSelloutParams) => void
  initialParams?: ValidacaoSelloutParams | null
}

export default function FiltersStep({ onGenerate, initialParams }: Props) {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light'
  const now = new Date()
  const [distributors, setDistributors] = useState<any[]>([])
  const [distSearch, setDistSearch] = useState('')
  const [distLoading, setDistLoading] = useState(true)

  // Parâmetros selecionados
  const [selectedDists, setSelectedDists] = useState<(string | number)[]>(
    initialParams?.distribuidorIds ?? [],
  )
  const [ano, setAno] = useState(initialParams?.ano ?? now.getFullYear())
  const [mes, setMes] = useState(initialParams?.mes ?? now.getMonth())
  const [mesCompleto, setMesCompleto] = useState(initialParams?.mesCompleto ?? true)
  const [semanas, setSemanas] = useState<number[]>(initialParams?.semanas ?? [])

  // Período: Mensal vs YTD
  const [tipoPeriodo, setTipoPeriodo] = useState<'mensal' | 'ytd' | 'diario'>(
    initialParams?.tipoPeriodo ?? 'mensal',
  )
  const [modoAgrupamentoDiario, setModoAgrupamentoDiario] = useState<'consolidado' | 'separado'>(
    initialParams?.modoAgrupamentoDiario ?? 'consolidado',
  )
  const [ytdModo, setYtdModo] = useState<'tudo' | 'custom'>(
    initialParams?.ytdModo ?? 'tudo',
  )
  const [mesesYtd, setMesesYtd] = useState<number[]>(
    initialParams?.mesesYtd ?? Array.from({ length: now.getMonth() + 1 }, (_, i) => i),
  )

  const handleSelectYtdTudoAteAgora = () => {
    setYtdModo('tudo')
    setMesesYtd(Array.from({ length: now.getMonth() + 1 }, (_, i) => i))
  }

  const handleSelectTodos12Meses = () => {
    setYtdModo('custom')
    setMesesYtd(Array.from({ length: 12 }, (_, i) => i))
  }

  const toggleMesYtd = (mIndex: number) => {
    setYtdModo('custom')
    setMesesYtd((prev) => {
      const next = prev.includes(mIndex) ? prev.filter((x) => x !== mIndex) : [...prev, mIndex]
      return next.sort((a, b) => a - b)
    })
  }

  // Cidades
  const [cidades, setCidades] = useState<string[]>([])
  const [selectedCidades, setSelectedCidades] = useState<string[]>(
    initialParams?.cidades ?? [],
  )
  const [cidadesSearch, setCidadesSearch] = useState('')
  const [cidadesLoading, setCidadesLoading] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const splitDropdownRef = useRef<HTMLDivElement>(null)

  // Estado de progresso para a geração em grupo
  const [groupProgress, setGroupProgress] = useState<{
    active: boolean
    current: number
    total: number
    distName: string
  } | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (splitDropdownRef.current && !splitDropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // ── Carrega distribuidores ──────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true
    setDistLoading(true)
    carregarDistribuidores()
      .then((data) => {
        if (isMounted) setDistributors(data || [])
      })
      .catch((err) => {
        console.error('[FiltersStep] Erro ao carregar distribuidores:', err)
        if (isMounted) setDistributors([])
      })
      .finally(() => {
        if (isMounted) setDistLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [])

  // ── Atualiza cidades quando distribuidores mudam ────────────────────────────
  useEffect(() => {
    let isMounted = true
    if (selectedDists.length === 0) {
      setCidades([])
      setSelectedCidades([])
      return
    }
    setCidadesLoading(true)
    buscarCidadesPorDistribuidores(selectedDists)
      .then((list) => {
        if (!isMounted) return
        setCidades(list || [])
        // Remove cidades selecionadas que não existem mais
        setSelectedCidades((prev) => prev.filter((c) => (list || []).includes(c)))
      })
      .catch((err) => {
        console.error('[FiltersStep] Erro ao carregar cidades:', err)
        if (isMounted) setCidades([])
      })
      .finally(() => {
        if (isMounted) setCidadesLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [selectedDists.join(',')])

  // ── Helpers de nome do distribuidor ────────────────────────────────────────
  const getDistName = (d: any) =>
    d.fantasy_name || d.name || d.nome || d.razao_social || `Dist. #${d.id || d._id}`

  const filteredDists = useMemo(() => {
    const term = distSearch.trim().toLowerCase()
    if (!term) return distributors
    return distributors.filter((d) => getDistName(d).toLowerCase().includes(term))
  }, [distributors, distSearch])

  const filteredCidades = useMemo(() => {
    const term = cidadesSearch.trim().toLowerCase()
    if (!term) return cidades
    return cidades.filter((c) => c.toLowerCase().includes(term))
  }, [cidades, cidadesSearch])

  // ── Toggle distribuidor ─────────────────────────────────────────────────────
  const toggleDist = (id: string | number) => {
    setSelectedDists((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id],
    )
  }

  const selectAllDists = () =>
    setSelectedDists(distributors.map((d) => d.id || d._id || d.cod_dist))

  const clearDists = () => {
    setSelectedDists([])
    setCidades([])
    setSelectedCidades([])
  }

  // ── Toggle semanas ──────────────────────────────────────────────────────────
  const isFullMonth = mesCompleto || semanas.length === 4

  const toggleSemana = (s: number) => {
    if (mesCompleto) {
      setMesCompleto(false)
      setSemanas([s])
      return
    }
    setSemanas((prev) => {
      const next = prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
      return next.sort((a, b) => a - b)
    })
  }

  const handleToggleMesCompleto = () => {
    if (mesCompleto || semanas.length === 4) {
      setMesCompleto(false)
      setSemanas([])
    } else {
      setMesCompleto(true)
      setSemanas([])
    }
  }

  // ── Toggle cidade ───────────────────────────────────────────────────────────
  const toggleCidade = (c: string) =>
    setSelectedCidades((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    )

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (selectedDists.length === 0) {
      toast.warning('Selecione ao menos um distribuidor para continuar.')
      return
    }

    const isAllDists =
      selectedDists.length > 0 &&
      distributors.length > 0 &&
      selectedDists.length >= distributors.length

    if (tipoPeriodo === 'diario') {
      setSubmitting(true)
      await new Promise((r) => setTimeout(r, 100))
      onGenerate({
        distribuidorIds: isAllDists ? [] : selectedDists,
        totalDistribuidoresCount: distributors.length,
        ano,
        mes,
        tipoPeriodo: 'diario',
        modoAgrupamentoDiario,
        mesCompleto: true,
        semanas: [],
        cidades: selectedCidades,
      })
      return
    }

    if (tipoPeriodo === 'ytd') {
      if (mesesYtd.length === 0) {
        toast.warning('Selecione ao menos um mês no período YTD.')
        return
      }
      setSubmitting(true)
      await new Promise((r) => setTimeout(r, 100))
      onGenerate({
        distribuidorIds: isAllDists ? [] : selectedDists,
        totalDistribuidoresCount: distributors.length,
        ano,
        mes: mesesYtd[mesesYtd.length - 1],
        tipoPeriodo: 'ytd',
        ytdModo,
        mesesYtd: [...mesesYtd].sort((a, b) => a - b),
        mesCompleto: true,
        semanas: [],
        cidades: selectedCidades,
      })
      return
    }

    const isAllWeeks = semanas.length === 4
    const finalMesCompleto = mesCompleto || isAllWeeks

    if (!finalMesCompleto && semanas.length === 0) {
      toast.warning('Selecione ao menos uma semana ou marque Mês Completo para continuar.')
      return
    }

    setSubmitting(true)
    await new Promise((r) => setTimeout(r, 100)) // flush state
    onGenerate({
      distribuidorIds: isAllDists ? [] : selectedDists,
      totalDistribuidoresCount: distributors.length,
      ano,
      mes,
      tipoPeriodo: 'mensal',
      mesCompleto: finalMesCompleto,
      semanas: finalMesCompleto && !isAllWeeks ? [] : [...semanas].sort((a, b) => a - b),
      cidades: selectedCidades,
    })
  }

  // ── Geração de Revisão em Grupo (Download ZIP) ──────────────────────────────
  const handleGerarRevisaoGrupo = async () => {
    if (selectedDists.length === 0) {
      toast.warning('Selecione ao menos um distribuidor para gerar a revisão em grupo.')
      return
    }

    if (tipoPeriodo === 'ytd' && mesesYtd.length === 0) {
      toast.warning('Selecione ao menos um mês no período YTD.')
      return
    }

    const isAllWeeks = semanas.length === 4
    const finalMesCompleto = tipoPeriodo === 'ytd' ? true : (mesCompleto || isAllWeeks)

    if (tipoPeriodo === 'mensal' && !finalMesCompleto && semanas.length === 0) {
      toast.warning('Selecione ao menos uma semana ou marque Mês Completo para continuar.')
      return
    }

    setDropdownOpen(false)

    // Obter lista completa dos distribuidores selecionados
    const distsSelecionados =
      selectedDists.length >= distributors.length && distributors.length > 0
        ? distributors
        : distributors.filter((d) => selectedDists.includes(d.id))

    if (distsSelecionados.length === 0) {
      toast.error('Nenhum distribuidor válido encontrado na seleção.')
      return
    }

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    const totalEtapas = distsSelecionados.length + (tipoPeriodo === 'ytd' ? 1 : 0)

    setGroupProgress({
      active: true,
      current: 0,
      total: totalEtapas,
      distName: distsSelecionados[0]?.name || '',
    })

    try {
      await processarRevisaoEmGrupo({
        distribuidores: distsSelecionados.map((d) => ({
          id: d.id,
          name: d.name,
          codDist: (d as any).cod_dist || (d as any).codDist,
        })),
        baseParams: {
          ano,
          mes: tipoPeriodo === 'ytd' ? (mesesYtd[mesesYtd.length - 1] ?? mes) : mes,
          tipoPeriodo: tipoPeriodo === 'ytd' ? 'ytd' : 'mensal',
          ytdModo,
          mesesYtd: tipoPeriodo === 'ytd' ? [...mesesYtd].sort((a, b) => a - b) : undefined,
          mesCompleto: finalMesCompleto,
          semanas: finalMesCompleto && !isAllWeeks ? [] : [...semanas].sort((a, b) => a - b),
          cidades: selectedCidades,
        },
        onProgress: (current, total, distName) => {
          setGroupProgress({
            active: true,
            current,
            total,
            distName,
          })
        },
        signal: abortController.signal,
      })

      toast.success(
        `Revisão em grupo concluída! Arquivo ZIP baixado com ${distsSelecionados.length} planilha(s).`
      )
    } catch (err: any) {
      if (err?.message?.includes('cancelada')) {
        toast.info('Geração de revisão em grupo cancelada.')
      } else {
        console.error('Erro ao gerar revisão em grupo:', err)
        toast.error(`Falha ao gerar revisão em grupo: ${err?.message || 'Erro de conexão'}`)
      }
    } finally {
      setGroupProgress(null)
      abortControllerRef.current = null
    }
  }

  const handleCancelGroupGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    setGroupProgress(null)
  }

  const anos = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i)

  return (
    <PageWrapper>
      <FilterCard>
        <FilterTitle>Validação de Sellout</FilterTitle>
        <FilterSubtitle>
          Configure os parâmetros e clique em <strong>Gerar Resumo</strong> para visualizar o
          painel.
        </FilterSubtitle>

        {/* ── Distribuidor ─────────────────────────────────────────────── */}
        <FilterSection>
          <FilterLabel>Distribuidor</FilterLabel>
          <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
            <SelectAllBtn type="button" onClick={selectAllDists}>
              Selecionar todos
            </SelectAllBtn>
            {selectedDists.length > 0 && (
              <SelectAllBtn
                type="button"
                onClick={clearDists}
                style={{ color: 'rgba(255,100,100,0.85)' }}
              >
                Limpar ({selectedDists.length})
              </SelectAllBtn>
            )}
          </div>
          <SearchInput
            placeholder="Buscar distribuidor..."
            value={distSearch}
            onChange={(e) => setDistSearch(e.target.value)}
          />
          <DistList>
            {distLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                <ThreeDot color="#a6c10b" size="small" />
              </div>
            ) : filteredDists.length === 0 ? (
              <span style={{ padding: '12px 8px', color: isLight ? '#64748b' : 'rgba(255,255,255,0.4)', fontSize: 13 }}>
                Nenhum distribuidor encontrado.
              </span>
            ) : (
              filteredDists.map((d) => {
                const id = d.id || d._id || d.cod_dist
                const selected = selectedDists.includes(id)
                return (
                  <DistItem key={id} $selected={selected} htmlFor={`dist-${id}`}>
                    <input
                      id={`dist-${id}`}
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleDist(id)}
                    />
                    <CheckboxBox $selected={selected}>
                      {selected && <FaCheck size={10} />}
                    </CheckboxBox>
                    {getDistName(d)}
                  </DistItem>
                )
              })
            )}
          </DistList>
        </FilterSection>

        <FilterDivider />

        {/* ── Período ──────────────────────────────────────────────────── */}
        <FilterSection>
          <FilterLabel>Tipo de Período</FilterLabel>
          <PeriodTypeContainer>
            <PeriodTypeButton
              type="button"
              $active={tipoPeriodo === 'mensal'}
              onClick={() => setTipoPeriodo('mensal')}
            >
              <FaCalendarAlt size={13} />
              <span>Mensal / Semanas</span>
            </PeriodTypeButton>
            <PeriodTypeButton
              type="button"
              $active={tipoPeriodo === 'ytd'}
              onClick={() => setTipoPeriodo('ytd')}
            >
              <FaChartLine size={13} />
              <span>Year to Date (Acumulado Ano)</span>
            </PeriodTypeButton>
            <PeriodTypeButton
              type="button"
              $active={tipoPeriodo === 'diario'}
              onClick={() => setTipoPeriodo('diario')}
            >
              <FaCalendarAlt size={13} />
              <span>Acompanhamento Diário</span>
            </PeriodTypeButton>
          </PeriodTypeContainer>

          {tipoPeriodo === 'mensal' && (
            <>
              <FilterLabel style={{ marginTop: 12 }}>Mês e Ano de Referência</FilterLabel>
              <SelectRow>
                <StyledSelect value={mes} onChange={(e) => setMes(Number(e.target.value))}>
                  {MONTH_NAMES.map((m, i) => (
                    <option key={i} value={i}>
                      {m}
                    </option>
                  ))}
                </StyledSelect>
                <StyledSelect value={ano} onChange={(e) => setAno(Number(e.target.value))}>
                  {anos.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </StyledSelect>
              </SelectRow>
            </>
          )}

          {tipoPeriodo === 'ytd' && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 12,
                  marginBottom: 6,
                }}
              >
                <FilterLabel style={{ margin: 0 }}>Ano e Meses Acumulados (YTD)</FilterLabel>
                <StyledSelect
                  value={ano}
                  onChange={(e) => setAno(Number(e.target.value))}
                  style={{ width: 'auto', minWidth: 100, padding: '4px 10px', fontSize: 13 }}
                >
                  {anos.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </StyledSelect>
              </div>

              <YtdPresetsRow>
                <YtdPresetButton
                  type="button"
                  $active={ytdModo === 'tudo'}
                  onClick={handleSelectYtdTudoAteAgora}
                  title="Seleciona de Janeiro até o mês atual"
                >
                  <FaBolt size={10} />
                  <span>Até o mês atual (Jan a {MONTH_NAMES[now.getMonth()]})</span>
                </YtdPresetButton>

                <YtdPresetButton
                  type="button"
                  $active={mesesYtd.length === 12}
                  onClick={handleSelectTodos12Meses}
                >
                  <span>Todos os 12 meses</span>
                </YtdPresetButton>
              </YtdPresetsRow>

              <MonthsGrid>
                {MONTH_NAMES.map((mName, idx) => {
                  const selected = mesesYtd.includes(idx)
                  const isCurrentMonth = idx === now.getMonth() && ano === now.getFullYear()
                  return (
                    <MonthChip
                      key={idx}
                      type="button"
                      $selected={selected}
                      onClick={() => toggleMesYtd(idx)}
                      title={isCurrentMonth ? `${mName} (Mês Atual)` : mName}
                    >
                      <span>{mName.substring(0, 3)}</span>
                      {selected && <FaCheck size={9} />}
                    </MonthChip>
                  )
                })}
              </MonthsGrid>
            </>
          )}

          {tipoPeriodo === 'diario' && (
            <>
              <FilterLabel style={{ marginTop: 12 }}>Mês e Ano de Referência</FilterLabel>
              <SelectRow>
                <StyledSelect value={mes} onChange={(e) => setMes(Number(e.target.value))}>
                  {MONTH_NAMES.map((m, i) => (
                    <option key={i} value={i}>
                      {m}
                    </option>
                  ))}
                </StyledSelect>
                <StyledSelect value={ano} onChange={(e) => setAno(Number(e.target.value))}>
                  {anos.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </StyledSelect>
              </SelectRow>

              <FilterLabel style={{ marginTop: 14 }}>Modo de Exibição dos Distribuidores</FilterLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 6 }}>
                <button
                  type="button"
                  onClick={() => setModoAgrupamentoDiario('consolidado')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: modoAgrupamentoDiario === 'consolidado'
                      ? '2px solid #a6c10b'
                      : isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.12)',
                    background: modoAgrupamentoDiario === 'consolidado'
                      ? isLight ? '#f0fdf4' : 'rgba(166, 193, 11, 0.15)'
                      : isLight ? '#ffffff' : 'rgba(0, 0, 0, 0.2)',
                    color: isLight ? '#0f172a' : '#ffffff',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'Montserrat, sans-serif',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <FaLayerGroup size={18} color={modoAgrupamentoDiario === 'consolidado' ? '#a6c10b' : '#64748b'} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem' }}>Acumular Distribuidores</div>
                    <div style={{ fontSize: '0.70rem', opacity: 0.75, marginTop: 2 }}>
                      Consolida todos os distribuidores selecionados na mesma matriz.
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setModoAgrupamentoDiario('separado')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: modoAgrupamentoDiario === 'separado'
                      ? '2px solid #a6c10b'
                      : isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.12)',
                    background: modoAgrupamentoDiario === 'separado'
                      ? isLight ? '#f0fdf4' : 'rgba(166, 193, 11, 0.15)'
                      : isLight ? '#ffffff' : 'rgba(0, 0, 0, 0.2)',
                    color: isLight ? '#0f172a' : '#ffffff',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'Montserrat, sans-serif',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <FaThList size={18} color={modoAgrupamentoDiario === 'separado' ? '#a6c10b' : '#64748b'} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem' }}>Mostrar Separados</div>
                    <div style={{ fontSize: '0.70rem', opacity: 0.75, marginTop: 2 }}>
                      Gera a matriz diária individual por distribuidor e visão geral.
                    </div>
                  </div>
                </button>
              </div>
            </>
          )}
        </FilterSection>

        {/* ── Semanas (apenas no modo Mensal) ───────────────────────────── */}
        {tipoPeriodo === 'mensal' && (
          <FilterSection>
            <FilterLabel>Semana</FilterLabel>
            <SemanasGrid>
              <SemanaCheckbox $selected={isFullMonth} htmlFor="semana-mes-completo">
                <input
                  id="semana-mes-completo"
                  type="checkbox"
                  checked={isFullMonth}
                  onChange={handleToggleMesCompleto}
                />
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    border: `2px solid ${isFullMonth ? '#a6c10b' : 'rgba(255,255,255,0.3)'}`,
                    background: isFullMonth ? '#a6c10b' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {isFullMonth && <FaCheck size={9} color="#001842" />}
                </span>
                Mês Completo
              </SemanaCheckbox>

              {[1, 2, 3, 4].map((s) => {
                const selected = !mesCompleto && semanas.includes(s)
                return (
                  <SemanaCheckbox key={s} $selected={selected} htmlFor={`semana-${s}`}>
                    <input
                      id={`semana-${s}`}
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleSemana(s)}
                    />
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        border: `2px solid ${selected ? '#a6c10b' : 'rgba(255,255,255,0.3)'}`,
                        background: selected ? '#a6c10b' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {selected && <FaCheck size={9} color="#001842" />}
                    </span>
                    Semana {s}
                  </SemanaCheckbox>
                )
              })}
            </SemanasGrid>
          </FilterSection>
        )}

        <FilterDivider />

        {/* ── Cidades ───────────────────────────────────────────────────── */}
        <FilterSection>
          <FilterLabel>
            Cidades{' '}
            <span style={{ color: isLight ? '#64748b' : 'rgba(255,255,255,0.4)', fontWeight: 400 }}>
              ({selectedCidades.length === 0 ? 'Todas' : `${selectedCidades.length} selecionada(s)`})
            </span>
          </FilterLabel>
          {selectedDists.length === 0 ? (
            <span style={{ color: isLight ? '#64748b' : 'rgba(255,255,255,0.35)', fontSize: 13 }}>
              Selecione ao menos um distribuidor para ver as cidades disponíveis.
            </span>
          ) : cidadesLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
              <ThreeDot color="#a6c10b" size="small" />
            </div>
          ) : (
            <>
              <SearchInput
                placeholder="Buscar cidade..."
                value={cidadesSearch}
                onChange={(e) => setCidadesSearch(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
                <SelectAllBtn
                  type="button"
                  onClick={() => setSelectedCidades([])}
                >
                  Todas as cidades
                </SelectAllBtn>
                {cidades.length > 0 && (
                  <SelectAllBtn
                    type="button"
                    onClick={() => setSelectedCidades([...cidades])}
                  >
                    Selecionar todas
                  </SelectAllBtn>
                )}
              </div>
              <DistList>
                {filteredCidades.map((c) => {
                  const selected = selectedCidades.includes(c)
                  return (
                    <DistItem key={c} $selected={selected} htmlFor={`cidade-${c}`}>
                      <input
                        id={`cidade-${c}`}
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleCidade(c)}
                      />
                      <CheckboxBox $selected={selected}>
                        {selected && <FaCheck size={10} />}
                      </CheckboxBox>
                      {c}
                    </DistItem>
                  )
                })}
              </DistList>
            </>
          )}
        </FilterSection>

        {/* ── Split Button: Gerar Resumo / Revisão em Grupo ─────────── */}
        {tipoPeriodo === 'diario' ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
            <GenerateButton
              type="button"
              disabled={submitting || selectedDists.length === 0}
              onClick={handleSubmit}
            >
              {submitting ? (
                <ButtonSpinner />
              ) : (
                'Gerar Acompanhamento Diário'
              )}
            </GenerateButton>
          </div>
        ) : (
          <SplitButtonWrapper ref={splitDropdownRef}>
          <SplitMainButton
            type="button"
            $loading={submitting}
            disabled={submitting || selectedDists.length === 0}
            onClick={handleSubmit}
            title="Visualizar resumo consolidado no painel"
          >
            <FaDesktop size={14} />
            <span>{submitting ? 'Carregando...' : 'Gerar Resumo'}</span>
          </SplitMainButton>

          <SplitDropdownToggle
            type="button"
            $open={dropdownOpen}
            disabled={submitting || selectedDists.length === 0}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            title="Opções de geração"
          >
            <FaChevronDown
              size={13}
              style={{
                transform: dropdownOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </SplitDropdownToggle>

          {dropdownOpen && (
            <SplitDropdownMenu>
              <SplitDropdownItem
                type="button"
                onClick={() => {
                  setDropdownOpen(false)
                  handleSubmit()
                }}
              >
                <div className="item-left">
                  <FaDesktop size={14} style={{ color: '#0284c7' }} />
                  <span>Visualizar no Painel</span>
                </div>
              </SplitDropdownItem>

              <SplitDropdownItem
                type="button"
                $isGroup
                onClick={handleGerarRevisaoGrupo}
              >
                <div className="item-left">
                  <FaFileArchive size={14} style={{ color: '#a6c10b' }} />
                  <span>Gerar Revisão em Grupo</span>
                </div>
                <span className="badge-zip">ZIP</span>
              </SplitDropdownItem>
            </SplitDropdownMenu>
          )}
        </SplitButtonWrapper>
        )}
      </FilterCard>

      {/* ── Modal de Progresso da Revisão em Grupo ───────────────────── */}
      {groupProgress?.active && (
        <GroupProgressModalOverlay>
          <GroupProgressModalCard>
            <div className="icon-wrap">
              <FaFileArchive />
            </div>
            <h3>Gerando Revisão em Grupo</h3>
            <p>
              Processando e gerando planilhas de validação de sellout por distribuidor:
              <span className="dist-current">
                {groupProgress.distName} ({groupProgress.current} de {groupProgress.total})
              </span>
            </p>

            <ProgressBarTrack>
              <ProgressBarFill
                $percent={
                  groupProgress.total > 0
                    ? Math.round((groupProgress.current / groupProgress.total) * 100)
                    : 0
                }
              />
            </ProgressBarTrack>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: isLight ? '#64748b' : '#94a3b8',
                fontWeight: 600,
              }}
            >
              <span>Progresso</span>
              <strong>
                {groupProgress.total > 0
                  ? Math.round((groupProgress.current / groupProgress.total) * 100)
                  : 0}
                %
              </strong>
            </div>

            <button
              type="button"
              className="btn-cancel"
              onClick={handleCancelGroupGeneration}
            >
              Cancelar Geração
            </button>
          </GroupProgressModalCard>
        </GroupProgressModalOverlay>
      )}
    </PageWrapper>
  )
}
